import React from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';

import {
    DeleteAttrPopup,
    TaskEditorPopup,
} from '../../../components/Dialog';
import TaskHeader from './TaskHeader';
import TaskListItems from './TaskListItems';

interface ClassificationTaskProps {
    choices: any[];
    setChoices: (value: any[]) => void;
    inputTypes: string[];
    setIsEdited: () => void;
    isTabEditable: boolean;
}

interface FormData {
    name: string;
    classificationType: string;
    inputType: string;
    attributes: Array<{
        attributeName: string;
        shortcut: string;
    }>;
}

const ClassificationTasks: React.FC<ClassificationTaskProps> = ({
    choices, setChoices, inputTypes, setIsEdited, isTabEditable,
}) => {
    const initialValues: any = {
        name: '',
        classificationType: 'single',
        inputType: inputTypes[0],
        attributes: [ {
            id: uuid(),
            attributeName: '',
            shortcut: '',
        } ],
    };

    const [ openTaskEditorPopup, setOpenTaskEditorPopup ] = React.useState(false);
    const [ openDeleteConfirmationPopup, setOpenDeleteConfirmationPopup ] = React.useState(false);
    const [ initialFormValues, setInitialFormValues ] = React.useState({ ...initialValues });
    const [ openIndex, setOpenIndex ] = React.useState(-1);
    const [ deleteIndex, setDeleteIndex ] = React.useState(-1);
    const { t } = useTranslation();

    const handleOpen = (index: number): void => {
        const initialValue = initialFormValues;
        const choice = choices[index];
        initialValue.name = choice['@_name'];
        initialValue.classificationType = choice['@_choice'];
        initialValue.inputType = choice['@_toName'];
        initialValue.attributes = [];
        choice['Choice']?.forEach((attribute: Record<string, any>) => {
            initialValue.attributes.push({
                id: uuid(),
                attributeName: attribute['@_value'],
                shortcut: attribute['@_hotkey'] ? attribute['@_hotkey'] : '',
            });
        });
        setInitialFormValues(initialValue);
        setOpenTaskEditorPopup(true);
        setOpenIndex(index);
    };

    const handleNewAttributeAdd = (): void => {
        setInitialFormValues({ ...initialValues });
        setOpenTaskEditorPopup(true);
        setOpenIndex(-1);
    };

    const handleSubmit = (value: FormData): void => {
        const newChoices = choices !== undefined && choices.length !== 0 ? [ ...choices ] : [];
        const newChoice = openIndex > -1 ? newChoices[openIndex] : {};
        newChoice['@_name'] = value.name;
        newChoice['@_choice'] = value.classificationType;
        newChoice['@_toName'] = value.inputType;
        newChoice['@_showInLine'] = openIndex > -1 ? newChoices[openIndex]['@_showInLine'] : true;
        newChoice['Choice'] = [];
        value.attributes.forEach((attribute: any) => {
            newChoice['Choice'].push({
                '@_value': attribute.attributeName,
                '@_hotkey': attribute.shortcut,
            });
        });
        if (openIndex > -1) {
            newChoices[openIndex] = newChoice;
        } else {
            newChoices.push(newChoice);
        }
        setChoices([ ...newChoices ]);
        setIsEdited();
        setOpenTaskEditorPopup(false);
    };

    const handleDelete = (index: number): void => {
        const newChoices = [ ...choices ];
        newChoices.splice(index, 1);
        setChoices(newChoices);
        setDeleteIndex(-1);
        setOpenDeleteConfirmationPopup(false);
        setIsEdited();
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(t('data_labelling_general_tab_name_required')),
        classificationType: Yup.string().required(t('data_labelling_general_tab_classification_type_required')),
        inputType: Yup.string().required(t('data_labelling_general_tab_input_type_required')),
        attributes: Yup.array().of(Yup.object({
            attributeName: Yup.string().required(t('data_labelling_general_tab_attribute_name_required')),
            shortcut: Yup.string(),
        })),
    });

    return (
        <>
            <DeleteAttrPopup
                title={t('classification_attr_delete_dialog_title_text')}
                infoText={t('classification_attr_delete_dialog_info_text')}
                setOpen={setOpenDeleteConfirmationPopup}
                open={openDeleteConfirmationPopup}
                handleDelete={(): void => handleDelete(deleteIndex)} />
            <TaskHeader
                headerText={t('a11y_classification_label')}
                handleOpen={handleNewAttributeAdd}
                isTabEditable={isTabEditable} />
            {choices?.length !== 0 && choices?.map((choice: any, index) => (
                <TaskListItems
                    label={choice}
                    key={uuid()}
                    option='Choice'
                    handleOpen={(): void => handleOpen(index)}
                    handleDelete={(): void => {
                        setDeleteIndex(index);
                        setOpenDeleteConfirmationPopup(true);
                    }}
                    isTabEditable={isTabEditable} />
            ))}
            <TaskEditorPopup
                type='classification'
                title={t('a11y_classification_label')}
                open={openTaskEditorPopup}
                setOpen={setOpenTaskEditorPopup}
                initialFormValues={initialFormValues}
                handleSubmit={handleSubmit}
                validationSchema={validationSchema}
                inputTypes={inputTypes} />
        </>
    );
};

export default ClassificationTasks;
